import React, { useState } from "react";
import StudentPageContainer from "@/components/layouts/StudentPageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PaginationComponent } from "@/components/PaginationComponent";
import { useMutationStartExamProgress } from "@/apis/exam_progress/startExamProgress";
import { useMutationStartExamPaper } from "@/apis/exam_progress/startExamPaper";
import { useQueryGetMyExamProgressList } from "@/apis/exam_progress/getMyExamProgressList";
import { getPaginationInfo } from "@/types/pagination";
import ExamsTable from "./ExamsTable";
import { useNavigate } from "react-router-dom";
import type { IExamProgress } from "@/apis/exam_progress/types";

const MyExamsPage: React.FC = () => {
  const navigate = useNavigate();

  // 페이지네이션을 위한 상태
  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 10,
  });

  // 학생의 시험 진행 상태 목록 가져오기 (API가 자동으로 학생의 시험만 필터링)
  const { data, isLoading, error } =
    useQueryGetMyExamProgressList(searchParams);

  // 시험 시작 API (기존 - 전체 시험 시작)
  const { mutate: startExam, isPending: isStarting } =
    useMutationStartExamProgress();

  // 시험 페이퍼 시작 API (새로 추가)
  const { mutate: startExamPaper, isPending: isStartingPaper } =
    useMutationStartExamPaper();

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  // 시험 시작 핸들러 (기존 - 전체 시험 시작)
  const handleStartExam = (id: string) => {
    // 시험 시작 전 확인
    if (
      window.confirm("시험을 시작하시겠습니까?\n시작하면 타이머가 작동합니다.")
    ) {
      startExam(id, {
        onSuccess: () => {
          // 시험 시작 성공 시 시험 응시 페이지로 이동
          navigate(`/my_exams/${id}/take`);
        },
        onError: (error) => {
          console.error("Failed to start exam:", error);
          // TODO: 에러 메시지 표시
        },
      });
    }
  };

  // 개별 시험지 시작 핸들러
  const handleStartPaper = (
    progressId: string,
    paperId: string,
    paperType: string
  ) => {
    // 시험지 시작 전 확인
    const paperTypeName =
      paperType === "reading_writing" ? "Reading & Writing" : "Math";
    if (
      window.confirm(
        `Are you sure you want to start the ${paperTypeName} exam?\nThe timer will start once you begin.`
      )
    ) {
      startExamPaper(
        { progressId, examPaperId: paperId },
        {
          onSuccess: (response) => {
            console.log("Paper started successfully:", response);
            // 시험 시작 성공 시 시험 응시 페이지로 이동
            navigate(`/my_exams/${progressId}/exam_paper/${paperId}/take`);
          },
          onError: (error) => {
            console.error("Failed to start paper:", error);
            // TODO: 에러 메시지 표시
          },
        }
      );
    }
  };

  return (
    <StudentPageContainer>
      <div className="space-y-6">
        <Heading>My Exams</Heading>
        <Text className="text-zinc-500">View all exams assigned to you.</Text>

        {/* 할당된 시험 테이블 컴포넌트 */}
        <ExamsTable
          exams={data?.items || []}
          isLoading={isLoading || isStarting || isStartingPaper}
          error={error}
          onStartPaper={handleStartPaper}
        />

        {/* 페이지네이션 */}
        {paginationInfo && paginationInfo.totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={
                paginationInfo.currentPage < paginationInfo.totalPages
              }
              hasPrevPage={paginationInfo.currentPage > 1}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </StudentPageContainer>
  );
};

export default MyExamsPage;
